import React, { useRef } from 'react';

import { sectionsMessages } from '../../../../../translates/sectionsMessages';
import { ContentWrapper } from '../../../../components/ContentWrapper/ContentWrapper';
import { ProgressBar } from '../../../../components/ProgressBar/ProgressBar';
import { useIntlReexport } from '../../../../hooks/useIntlReexport';
import { HistoryItem } from '../../../../store/reducers/currentSequencedFlowPositionReducer';
import { useContentRequirementsContext } from '../../../RestrictNavigation/ContentRequirementsProvider';
import { ItemViewed } from '../../../tracking/components/functional/ItemViewed';
import { useSequencedContentFlowContext } from '../../context/SequencedContentFlowContext';
import { ContentRenderer } from '../ContentRenderer';
import { LinearFlowStepAudioPlayer } from '../LinearFlowStepAudioPlayer';
import { Navigation } from '../Navigation';
import { useContentDisplayed } from './hooks/useContentDisplayed';
import { isSection, Section } from '@ael/core-api';
import { Stack } from 'immutable';
import { FroalaEditorsSettings } from 'src/config/configs';

export interface MobileSequencedFlowDataProps {
  onGoToSection: (section: Section) => void;
  history: Stack<HistoryItem>;
  isStepAndDismissable: boolean;
}

export interface MobileSequencedFlowCallbackProps {
  onDismissSequencedContent: () => void;
}

interface MobileSequencedFlowProps extends MobileSequencedFlowCallbackProps, MobileSequencedFlowDataProps {
  froalaEditorsSettings: FroalaEditorsSettings;
}

export const MobileSequencedFlow: React.FC<MobileSequencedFlowProps> = ({
  onGoToSection,
  onDismissSequencedContent,
  isStepAndDismissable,
  history,
  froalaEditorsSettings,
}) => {
  const { formatMessage } = useIntlReexport();
  const { currentContentItem, isLastContentItem, sequencedFlowItem, currentSection } = useSequencedContentFlowContext();
  const requirements = useContentRequirementsContext();
  const { contentDisplayed } = useContentDisplayed(currentContentItem);
  const toolbarRef = useRef(null);

  return (
    <>
      <ContentWrapper>
        <ContentWrapper.Modal collapsed={!contentDisplayed}>
          <ContentWrapper.TopBar>
            <ProgressBar
              steps={[
                {
                  id: currentContentItem.id,
                  selected: true,
                  disabled: false,
                },
              ]}
            />
          </ContentWrapper.TopBar>
          {contentDisplayed && (
            <ContentWrapper.MobileContent key={currentContentItem.id}>
              <ContentWrapper.Title
                title={
                  isSection(currentContentItem) ? formatMessage(sectionsMessages.choose) : currentContentItem.title
                }
                onClose={isStepAndDismissable ? onDismissSequencedContent : undefined}
                small
              />
              <ContentWrapper.Body noHorizontalPadding>
                <ContentRenderer
                  currentContentItem={currentContentItem}
                  currentSectionItem={currentSection}
                  onGoToSection={onGoToSection}
                  froalaEditorsSettings={froalaEditorsSettings}
                />
              </ContentWrapper.Body>
            </ContentWrapper.MobileContent>
          )}
        </ContentWrapper.Modal>
        <ContentWrapper.Toolbar ref={toolbarRef}>
          <>
            {isLastContentItem && <ItemViewed itemId={sequencedFlowItem.id} isCompleted={requirements.fulfilled} />}
            <Navigation
              isDisplayedOnMobile={true}
              history={history}
              ref={toolbarRef}
              audioPlayer={<LinearFlowStepAudioPlayer linearFlowStep={currentContentItem} isMobile={true} />}
            />
          </>
        </ContentWrapper.Toolbar>
        <ContentWrapper.Caption />
      </ContentWrapper>
      {!contentDisplayed && requirements.fulfilled && <ItemViewed itemId={currentContentItem.id} isCompleted={true} />}
    </>
  );
};
